// @ts-nocheck
import { capitalize, filter, flatten, map, sortBy, uniq } from 'lodash';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useIntl } from 'react-intl';
import { BoxV2, Checkbox, DateRangePicker, Input, Selector } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { worldTimezones } from 'sonata-static-storage/lib/samples/worldTimezones.samples';

import { getSelectorValue } from '@/modules/reporting/ReportingSectionAdvancedFilter/helpers';
import { validEmailAddress } from '@/services/helpers';
import { useReportDownloadSectionContext } from '@/ts-modules/reporting/ReportDownload/ReportDownloadSection.context';
import { ScheduleReportStyled } from '@/ts-modules/reporting/ReportDownload/ScheduleReport/ScheduleReport.styled';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type Props = {
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

function ScheduleReport({ user, userRoles }: Props) {
  const { formatMessage } = useIntl();
  const shortDateFormat = taptap.app.i18n_dates.getDateLocalizations().shortDate;

  const { isSchedule, scheduleConfig, setScheduleConfig } = useReportDownloadSectionContext();

  const periodicityOptions = ['once', 'daily', 'weekly', 'monthly'].map(opt => ({
    id: opt,
    label: formatMessage({
      defaultMessage: capitalize(opt),
      id: `scheduleReport.periodicitySelector.options.${opt}`
    })
  }));
  const periodicityOptionsSelected = periodicityOptions.filter(opt => opt.id === scheduleConfig.periodicity);

  const hoursOptions: { id: string; label: string }[] = [];
  const auxMoment = moment();
  for (let i = 0; i < 24; i++) {
    auxMoment.set('hours', i);
    hoursOptions.push({
      id: i.toString(),
      label: auxMoment.format('hh A')
    });
  }
  const hoursOptionsSelected = hoursOptions.filter(opt => scheduleConfig.hours.includes(opt.id));

  const monthOptions: { id: number; label: string }[] = [];
  for (let i = 1; i < 31; i++) {
    monthOptions.push({
      id: i,
      label: i.toString()
    });
  }
  const monthOptionsSelected = monthOptions.filter(opt => scheduleConfig.monthDay === opt.id);

  const weekOptions: { id: number; label: string }[] = [];
  for (let i = 1; i < 8; i++) {
    weekOptions.push({
      id: i,
      label: formatMessage({
        defaultMessage: 'weekDay',
        id: `scheduleReport.weekDays.weekDay${i}`
      })
    });
  }
  const weekOptionsSelected = weekOptions.filter(opt => scheduleConfig.weekDay === opt.id);

  const multipleHourSelectorEnabled =
    checkUserResourcePermissions('reportingScheduleMultipleHours', user, userRoles) &&
    scheduleConfig.periodicity === 'daily';

  const allTimeZones = sortBy(
    uniq(
      flatten(
        map(
          filter(worldTimezones, timezoneGroup => {
            return timezoneGroup.group !== 'UTC' && timezoneGroup.group !== 'Manual Offsets';
          }),
          'zones'
        )
      )
    ),
    'name'
  );

  const timeZoneOptions = allTimeZones.map(zone => ({
    id: zone.value,
    label: zone.name
  }));

  const timeZoneValue = getSelectorValue(timeZoneOptions, scheduleConfig.timezone);

  const handlePeriodicitySelectorChange = (periodicity: { id: string }) => {
    setScheduleConfig({
      ...scheduleConfig,
      ...(periodicity.id === 'daily' && { hours: scheduleConfig.hours.slice(0, 1) }),
      periodicity: periodicity.id
    });
  };

  const isOutsideRange = (day: Moment) => {
    return day.isBefore(moment(), 'day');
  };

  const handleDatesChange = ({ endDate, startDate }: { endDate: Moment | null; startDate: Moment | null }) => {
    setScheduleConfig({
      ...scheduleConfig,
      timeFrame: {
        ...scheduleConfig.timeFrame,
        end: endDate,
        start: startDate
      }
    });
  };

  const handleDateChange = (date: Moment | null) => {
    const newDate = moment.utc(date).startOf('day');
    setScheduleConfig({
      ...scheduleConfig,
      timeFrame: {
        ...scheduleConfig.timeFrame,
        end: newDate ? moment.utc(newDate).endOf('day') : null,
        start: newDate ? moment.utc(newDate).startOf('day') : null
      }
    });
  };

  const handleHoursChange = (hour: { id: string; label: string } | { id: string; label: string }[]) => {
    const hours = Array.isArray(hour) ? hour.map(h => h.id) : [hour.id];
    setScheduleConfig({
      ...scheduleConfig,
      hours
    });
  };

  const handleMonthChange = (month: { id: number; label: string }) => {
    setScheduleConfig({
      ...scheduleConfig,
      monthDay: month.id
    });
  };

  const handleWeekChange = (weekDay: { id: number; label: string }) => {
    setScheduleConfig({
      ...scheduleConfig,
      weekDay: weekDay.id
    });
  };

  const handleTimeZoneChange = (timezone: string) => {
    setScheduleConfig({
      ...scheduleConfig,
      timezone
    });
  };

  const handleEmailChange = (value: string | FileList | null) => {
    const emails = value?.length ? [value.toString()] : [];
    setScheduleConfig({
      ...scheduleConfig,
      emails
    });
  };

  const handleCheckboxClick = (checked: boolean) => {
    setScheduleConfig(checked ? scheduleConfig : null);
  };

  const emailsAreValid = flatten(scheduleConfig.emails.map(i => i.split(','))).every(email => validEmailAddress(email));

  return (
    <ScheduleReportStyled className="scheduleReport">
      <div className="scheduleReport__titleContent">
        <Checkbox
          active={isSchedule}
          id="scheduleReport-checkbox"
          isSwitch
          onClickAction={(_, checked) => handleCheckboxClick(checked)}
          type="switch"
        />
        <span className="scheduleReport__title h2 alignLeft fontRegular">
          {formatMessage({
            defaultMessage: 'Scheduled Report',
            id: 'scheduleReport.title'
          })}
        </span>
      </div>
      {isSchedule && (
        <BoxV2 id="scheduleReport-box">
          <BoxV2.Body bgColor="boxBgColor01" borderRadius="4px" className="scheduleReport__boxContent" padding="16px">
            <div className="scheduleReport__optionsContainer">
              <Selector
                className="scheduleReport__periodicitySelector"
                label={formatMessage({
                  defaultMessage: 'Periodicity',
                  id: 'scheduleReport.periodicitySelector.title'
                })}
                onChange={handlePeriodicitySelectorChange}
                options={periodicityOptions}
                type="simple"
                value={periodicityOptionsSelected}
              />
              <div className="scheduleReport__dateContainer">
                <DateRangePicker
                  {...(scheduleConfig.periodicity === 'once'
                    ? {
                        id: 'scheduleReport-date-picker',
                        onDateChange: handleDateChange,
                        showClearDates: false,
                        ...(scheduleConfig.timeFrame.start && { date: scheduleConfig.timeFrame.start })
                      }
                    : {
                        endDateId: 'scheduleReport-end-date',
                        minimumNights: 0,
                        onDatesChange: handleDatesChange,
                        showClearDates: true,
                        startDateId: 'scheduleReport-start-date',
                        ...(scheduleConfig.timeFrame.end && { endDate: scheduleConfig.timeFrame.end }),
                        ...(scheduleConfig.timeFrame.start && { startDate: scheduleConfig.timeFrame.start })
                      })}
                  displayFormat={shortDateFormat}
                  isOutsideRange={isOutsideRange}
                  label={formatMessage({
                    defaultMessage: 'Period of time',
                    id: 'scheduleReport.periodTimeSelector'
                  })}
                  numberOfMonths={2}
                  singlePicker={scheduleConfig.periodicity === 'once'}
                />
              </div>
              <div className="scheduleReport__hoursContainer">
                {scheduleConfig.periodicity === 'monthly' && (
                  <Selector<{ id: number; label: string }>
                    className="scheduleReport__monthSelector"
                    label={formatMessage({
                      defaultMessage: 'Month & Hour',
                      id: 'scheduleReport.monthSelector'
                    })}
                    onChange={handleMonthChange}
                    options={monthOptions}
                    type="simple"
                    value={monthOptionsSelected}
                  />
                )}
                {scheduleConfig.periodicity === 'weekly' && (
                  <Selector<{ id: number; label: string }>
                    className="scheduleReport__weekSelector"
                    label={formatMessage({
                      defaultMessage: 'Week Day & Hour',
                      id: 'scheduleReport.weekSelector'
                    })}
                    onChange={handleWeekChange}
                    options={weekOptions}
                    type="simple"
                    value={weekOptionsSelected}
                  />
                )}
                <Selector<{ id: string; label: string }>
                  className="scheduleReport__hourSelector"
                  label={
                    scheduleConfig.periodicity === 'weekly' || scheduleConfig.periodicity === 'monthly'
                      ? ''
                      : formatMessage({
                          defaultMessage: 'Hours',
                          id: 'scheduleReport.hoursSelector'
                        })
                  }
                  onChange={handleHoursChange}
                  options={hoursOptions}
                  showSelectedItems={false}
                  type={multipleHourSelectorEnabled ? 'multiple' : 'simple'}
                  value={hoursOptionsSelected}
                />
              </div>
              <Selector<{ id: string; label: string }>
                className="scheduleReport__timezoneSelector"
                label={formatMessage({
                  defaultMessage: 'Timezone',
                  id: 'scheduleReport.timezone'
                })}
                onChange={option => handleTimeZoneChange(option.id)}
                options={timeZoneOptions}
                type="simple"
                value={timeZoneValue}
              />
            </div>
            <Input
              id="scheduleReport__email"
              inputStatus={emailsAreValid ? 'default' : 'wrong'}
              label={formatMessage({
                defaultMessage: 'Email',
                id: 'scheduleReport.email'
              })}
              name="scheduleReportEmail"
              notFloatingPlaceholder
              onChange={handleEmailChange}
              placeholder={formatMessage({
                defaultMessage: 'Ex: Jhon@mail.com',
                id: 'scheduleReport.emailPlaceholder'
              })}
              shape="squareUI"
              type="email"
              value={scheduleConfig.emails[0]}
            />
          </BoxV2.Body>
        </BoxV2>
      )}
    </ScheduleReportStyled>
  );
}

export default ScheduleReport;
